import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Col, Row, CardImg } from "reactstrap";
import { X, UploadCloud } from "react-feather";
import AddSvg from "../../assets/Add.svg"; // Ensure correct path
import { removeFile, uploadSingleFile } from "../../../../../api-handle/fileApi";
import { sanitizeArabicFieldEvent } from "../../../../../Services/arabicInput";

const PUBLIC_BASE_URL = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/?$/, "/");

const BannerFormModal = ({ isOpen, toggle, bannerData, onSave }) => {
  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    image: null,
    bannerType: ""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens or bannerData changes
  useEffect(() => {
    if (isOpen) {
      setFormData(bannerData || { title: "", titleAr: "", image: null, bannerType: "" });
      setErrors({});
    }
  }, [isOpen, bannerData]);

  // Handle input changes with error handling
  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    let newErrors = { ...errors, [name]: "" };

    if (name === "image" && files.length) {
      setLoading(true);
      try {
        // Validate file type and size (max 5MB)
        const file = files[0];
        const validTypes = ["image/jpeg", "image/png", "image/gif"];
        if (!validTypes.includes(file.type)) {
          newErrors.image = "Please upload a valid image (JPEG, PNG, or GIF)";
          setErrors(newErrors);
          setLoading(false);
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          newErrors.image = "Image size must be less than 5MB";
          setErrors(newErrors);
          setLoading(false);
          return;
        }

        const uploadedImage = await uploadSingleFile(file);
        if (!uploadedImage || !uploadedImage.fileName) {
          throw new Error("File upload failed");
        }
        setFormData({ ...formData, image: uploadedImage.fileName });
      } catch (error) {
        newErrors.image = error.message || "Failed to upload image";
        setErrors(newErrors);
      } finally {
        setLoading(false);
      }
    } else {
      // Validate text inputs and bannerType in real-time
      if (name === "title" && !value.trim()) {
        newErrors.title = "Title cannot be empty";
      }
      if (name === "bannerType" && !["gym", "academy", "marketplace", "home"].includes(value)) {
        newErrors.bannerType = "Please select a valid banner type";
      }
      setFormData({ ...formData, [name]: sanitizeArabicFieldEvent(e) });
      setErrors(newErrors);
    }
  };

  // Validate form fields before saving
  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.image) newErrors.image = "Banner image is required";
    if (!["gym", "academy", "marketplace", "home"].includes(formData.bannerType)) newErrors.bannerType = "Banner type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (validate()) {
      onSave(formData);
      toggle();
    }
  };


  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="font-sans" style={{ maxWidth: "600px" }}>
      <ModalHeader toggle={toggle} className="bg-dark text-white font-weight-bold">
        {bannerData ? "Edit Banner" : "Add New Banner"}
      </ModalHeader>
      <ModalBody className="bg-light p-4">
        <Form>
          {/* Image Preview */}
          <Row className="mb-3">
            <Col xs="12" className="text-center">
              {formData.image ? (
                <div className="position-relative d-inline-block">
                  <CardImg
                    src={PUBLIC_BASE_URL + "/" + formData.image}
                    alt="Banner Preview"
                    className="img-fluid rounded shadow-sm"
                    style={{ maxHeight: "200px", objectFit: "cover" }}
                  />
                  <Button
                    color="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 p-1 border-0 rounded-circle"
                    onClick={() => {
                      if (formData.image) {
                        removeFile(formData.image);
                        setFormData({ ...formData, image: null });
                      }
                    }}
                  >
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <CardImg
                  src={AddSvg}
                  alt="Add Banner"
                  className="img-fluid rounded shadow-sm border"
                  style={{ maxHeight: "150px", objectFit: "contain" }}
                />
              )}
            </Col>
          </Row>

          {/* Upload Field */}
          <Row className="mb-3">
            <Col xs="12">
              <label
                className={`d-flex flex-column align-items-center justify-content-center border border-2 border-dashed rounded p-3 ${loading ? "bg-secondary cursor-not-allowed" : "bg-white cursor-pointer"}`}
                style={{ height: "100px" }}
              >
                <UploadCloud size={24} className="text-primary" />
                <span className="text-muted small mt-2">
                  {loading ? "Uploading..." : "Upload Banner Image"}
                </span>
                <Input
                  type="file"
                  name="image"
                  onChange={handleChange}
                  disabled={loading}
                  hidden
                  accept="image/*"
                />
              </label>
              {errors.image && <p className="text-danger small mt-2">{errors.image}</p>}
            </Col>
          </Row>

          {/* Title Fields */}
          <Row className="mb-3">
            <Col md="6">
              <FormGroup>
                <Label className="font-weight-medium text-dark">Title</Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter Banner Title"
                  className="form-control border rounded"
                />
                {errors.title && <p className="text-danger small mt-1">{errors.title}</p>}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="font-weight-medium text-dark">Title (Arabic)</Label>
                <Input
                  type="text"
                  name="titleAr"
                  value={formData.titleAr}
                  onChange={handleChange}
                  placeholder="Enter Banner Title in Arabic"
                  className="form-control border rounded"
                  dir="rtl"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Banner Type Field */}
          <FormGroup>
            <Label className="font-weight-medium text-dark">Banner Type</Label>
            <Input
              type="select"
              name="bannerType"
              value={formData.bannerType}
              onChange={handleChange}
              className="form-control border rounded"
            >
              <option value="">Select Banner Type</option>
              <option value="gym">Gym</option>
              <option value="academy">Academy</option>
              <option value="marketplace">Marketplace</option>
              <option value="home">Home</option>
            </Input>
            {errors.bannerType && <p className="text-danger small mt-1">{errors.bannerType}</p>}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button
          color="secondary"
          onClick={toggle}
          className="btn btn-secondary"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? "Saving..." : "Save Banner"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BannerFormModal;